import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Users, Building2, FileCheck, Ticket, Activity, Shield, Bell, MessageSquare, Settings, GraduationCap } from 'lucide-react';

const ADMIN_API = '/api/platform-admin';

async function adminFetch<T>(path: string, token: string): Promise<T> {
  const res = await fetch(`${ADMIN_API}${path}`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok) throw new Error(`${res.status}`);
  return res.json() as Promise<T>;
}

// ── Login Form ───────────────────────────────────────────

function AdminLogin({ onLogin }: { readonly onLogin: (token: string, admin: { email: string; name: string }) => void }) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    try {
      const res = await fetch(`${ADMIN_API}/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password }),
      });
      if (!res.ok) { setError('Invalid credentials'); return; }
      const data = await res.json() as { token: string; admin: { email: string; name: string } };
      onLogin(data.token, data.admin);
    } catch { setError('Login failed'); }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-background">
      <form onSubmit={handleSubmit} className="w-full max-w-sm space-y-4 rounded-lg border border-border bg-card p-8">
        <div className="text-center">
          <Shield className="mx-auto h-8 w-8 text-teal-400" />
          <h1 className="mt-2 text-xl font-bold">NOETED Platform Admin</h1>
        </div>
        {error && <p className="text-sm text-red-400">{error}</p>}
        <input className="w-full rounded border border-border bg-background px-3 py-2 text-sm" placeholder="Email" value={email} onChange={e => setEmail(e.target.value)} />
        <input className="w-full rounded border border-border bg-background px-3 py-2 text-sm" type="password" placeholder="Password" value={password} onChange={e => setPassword(e.target.value)} />
        <button type="submit" className="w-full rounded bg-teal-600 px-4 py-2 text-sm font-medium text-white hover:bg-teal-500">Sign In</button>
      </form>
    </div>
  );
}

// ── Stat Card ────────────────────────────────────────────

function StatCard({ label, value, icon: Icon, color = 'text-teal-400' }: {
  readonly label: string; readonly value: string | number; readonly icon: typeof Users; readonly color?: string;
}) {
  return (
    <div className="rounded-lg border border-border bg-card p-5">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-xs text-muted-foreground">{label}</p>
          <p className="mt-1 text-2xl font-bold">{value}</p>
        </div>
        <Icon className={`h-8 w-8 ${color} opacity-60`} />
      </div>
    </div>
  );
}

// ── Tabs ─────────────────────────────────────────────────

type Tab = 'overview' | 'tenants' | 'analytics' | 'training' | 'health' | 'tickets' | 'announcements' | 'audit' | 'settings';

const TABS: { value: Tab; label: string; icon: typeof Users }[] = [
  { value: 'overview', label: 'Overview', icon: Activity },
  { value: 'tenants', label: 'Tenants', icon: Building2 },
  { value: 'analytics', label: 'Analytics', icon: FileCheck },
  { value: 'training', label: 'Training', icon: GraduationCap },
  { value: 'health', label: 'System Health', icon: Shield },
  { value: 'tickets', label: 'Support', icon: Ticket },
  { value: 'announcements', label: 'Announcements', icon: Bell },
  { value: 'audit', label: 'Audit Log', icon: MessageSquare },
  { value: 'settings', label: 'Settings', icon: Settings },
];

interface PlatformTrainingAnalytics {
  totalPackages: number;
  totalAssignments: number;
  completedAssignments: number;
  overdueAssignments: number;
  completionRate: number;
  averageCompletionTimeHours: number | null;
  byTenant: {
    tenantId: string;
    tenantName: string;
    packages: number;
    assigned: number;
    completed: number;
    overdue: number;
    completionRate: number;
  }[];
}

// ── Password Change Form ─────────────────────────────────

function PasswordChangeForm({ token, onLogout }: { readonly token: string; readonly onLogout: () => void }) {
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (newPassword.length < 12) { setError('New password must be at least 12 characters'); return; }
    if (newPassword !== confirmPassword) { setError('Passwords do not match'); return; }

    try {
      const res = await fetch(`${ADMIN_API}/me/password`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ currentPassword, newPassword }),
      });

      if (!res.ok) {
        const data = await res.json() as { error?: string };
        setError(data.error ?? 'Failed to change password');
        return;
      }

      setSuccess(true);
      setTimeout(() => {
        localStorage.removeItem('platform_admin_token');
        onLogout();
      }, 2000);
    } catch { setError('Request failed'); }
  };

  if (success) {
    return (
      <div className="space-y-4">
        <h1 className="text-2xl font-bold">Settings</h1>
        <div className="rounded-lg border border-emerald-700 bg-emerald-900/20 p-6 text-center">
          <p className="text-emerald-400 font-medium">Password changed successfully</p>
          <p className="text-sm text-muted-foreground mt-1">Redirecting to login...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-bold">Settings</h1>
      <div className="max-w-md">
        <h2 className="text-lg font-semibold mb-4">Change Password</h2>
        <form onSubmit={handleSubmit} className="space-y-4">
          {error && <p className="text-sm text-red-400 bg-red-900/20 rounded p-2">{error}</p>}
          <div>
            <label className="block text-sm font-medium mb-1">Current Password</label>
            <input type="password" className="w-full rounded border border-border bg-background px-3 py-2 text-sm"
              value={currentPassword} onChange={e => setCurrentPassword(e.target.value)} required />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">New Password</label>
            <input type="password" className="w-full rounded border border-border bg-background px-3 py-2 text-sm"
              value={newPassword} onChange={e => setNewPassword(e.target.value)} required minLength={12} />
            <p className="text-xs text-muted-foreground mt-1">Minimum 12 characters</p>
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Confirm New Password</label>
            <input type="password" className="w-full rounded border border-border bg-background px-3 py-2 text-sm"
              value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)} required />
          </div>
          <button type="submit" className="rounded bg-teal-600 px-4 py-2 text-sm font-medium text-white hover:bg-teal-500">
            Change Password
          </button>
        </form>
      </div>
    </div>
  );
}

// ── Main Dashboard ───────────────────────────────────────

export default function PlatformDashboard() {
  const [token, setToken] = useState<string | null>(localStorage.getItem('platform_admin_token'));
  const [admin, setAdmin] = useState<{ email: string; name: string } | null>(null);
  const [activeTab, setActiveTab] = useState<Tab>('overview');

  if (!token) {
    return <AdminLogin onLogin={(t, a) => { setToken(t); setAdmin(a); localStorage.setItem('platform_admin_token', t); }} />;
  }

  const { data: overview } = useQuery({
    queryKey: ['platform-overview', token],
    queryFn: () => adminFetch<{ data: Record<string, unknown> }>('/analytics/overview', token),
    staleTime: 30_000,
    enabled: activeTab === 'overview',
  });

  const { data: tenants } = useQuery({
    queryKey: ['platform-tenants', token],
    queryFn: () => adminFetch<{ data: Record<string, unknown>[] }>('/tenants', token),
    staleTime: 30_000,
    enabled: activeTab === 'tenants',
  });

  const { data: health } = useQuery({
    queryKey: ['platform-health', token],
    queryFn: () => adminFetch<{ data: Record<string, unknown> }>('/health/system', token),
    staleTime: 10_000,
    enabled: activeTab === 'health',
  });

  const { data: tickets } = useQuery({
    queryKey: ['platform-tickets', token],
    queryFn: () => adminFetch<{ data: Record<string, unknown>[] }>('/tickets', token),
    staleTime: 30_000,
    enabled: activeTab === 'tickets',
  });

  const { data: announcements } = useQuery({
    queryKey: ['platform-announcements', token],
    queryFn: () => adminFetch<{ data: Record<string, unknown>[] }>('/announcements', token),
    staleTime: 30_000,
    enabled: activeTab === 'announcements',
  });

  const { data: auditLog } = useQuery({
    queryKey: ['platform-audit', token],
    queryFn: () => adminFetch<{ data: Record<string, unknown>[] }>('/audit', token),
    staleTime: 30_000,
    enabled: activeTab === 'audit',
  });

  const { data: training } = useQuery({
    queryKey: ['platform-training', token],
    queryFn: () => adminFetch<{ data: PlatformTrainingAnalytics }>('/analytics/training', token),
    staleTime: 30_000,
    enabled: activeTab === 'training',
  });

  const ov = overview?.data ?? {};
  const tenantStats = (ov.tenants ?? {}) as Record<string, number>;

  const handleLogout = () => {
    setToken(null);
    setAdmin(null);
    localStorage.removeItem('platform_admin_token');
  };

  return (
    <div className="flex min-h-screen bg-background">
      {/* Sidebar */}
      <aside className="w-56 border-r border-border bg-card p-4">
        <div className="mb-6 flex items-center gap-2">
          <Shield className="h-5 w-5 text-teal-400" />
          <span className="font-bold text-sm">Admin Console</span>
        </div>
        <nav className="space-y-1">
          {TABS.map(tab => (
            <button key={tab.value} onClick={() => setActiveTab(tab.value)}
              className={`flex w-full items-center gap-2 rounded px-3 py-2 text-sm ${activeTab === tab.value ? 'bg-teal-900/30 text-teal-400' : 'text-muted-foreground hover:text-foreground'}`}>
              <tab.icon className="h-4 w-4" /> {tab.label}
            </button>
          ))}
        </nav>
        <div className="mt-8 border-t border-border pt-4">
          <p className="text-xs text-muted-foreground">{admin?.email ?? 'Admin'}</p>
          <button onClick={handleLogout} className="mt-2 text-xs text-red-400 hover:underline">Sign Out</button>
        </div>
      </aside>

      {/* Content */}
      <main className="flex-1 p-8">
        {activeTab === 'overview' && (
          <div className="space-y-6">
            <h1 className="text-2xl font-bold">Platform Overview</h1>
            <div className="grid grid-cols-4 gap-4">
              <StatCard label="Total Tenants" value={tenantStats.total ?? 0} icon={Building2} />
              <StatCard label="Active Users (30d)" value={ov.activeUsers as number ?? 0} icon={Users} />
              <StatCard label="Reviews This Month" value={ov.reviewsThisPeriod as number ?? 0} icon={FileCheck} />
              <StatCard label="Open Tickets" value={ov.openTickets as number ?? 0} icon={Ticket} color="text-amber-400" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <StatCard label="Active Tenants" value={tenantStats.active ?? 0} icon={Building2} color="text-emerald-400" />
              <StatCard label="Trial Tenants" value={tenantStats.trial ?? 0} icon={Building2} color="text-blue-400" />
            </div>
          </div>
        )}

        {activeTab === 'tenants' && (
          <div className="space-y-4">
            <h1 className="text-2xl font-bold">Tenants</h1>
            <div className="space-y-2">
              {(tenants?.data ?? []).map((t) => (
                <div key={t.id as string} className="rounded-lg border border-border bg-card p-4 flex items-center justify-between">
                  <div>
                    <p className="font-medium text-sm">{t.name as string}</p>
                    <p className="text-xs text-muted-foreground">{t.user_count as number} users &middot; {t.project_count as number} projects &middot; {t.plan_type as string ?? 'trial'}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className={`rounded px-2 py-0.5 text-xs font-medium ${t.status === 'active' ? 'bg-emerald-900/30 text-emerald-400' : 'bg-amber-900/30 text-amber-400'}`}>
                      {t.status as string ?? 'active'}
                    </span>
                    <span className="text-xs text-muted-foreground">
                      {new Date(t.created_at as string).toLocaleDateString()}
                    </span>
                  </div>
                </div>
              ))}
              {(tenants?.data ?? []).length === 0 && <p className="text-sm text-muted-foreground py-8 text-center">No tenants yet</p>}
            </div>
          </div>
        )}

        {activeTab === 'health' && (
          <div className="space-y-4">
            <h1 className="text-2xl font-bold">System Health</h1>
            {health?.data && (
              <div className="grid grid-cols-2 gap-4">
                <div className="rounded-lg border border-border bg-card p-5">
                  <h3 className="text-sm font-medium mb-2">Database</h3>
                  <p className="text-xs text-muted-foreground">Connections: {(health.data.database as Record<string, unknown>)?.connections as number}</p>
                  <p className="text-xs text-emerald-400 mt-1">Healthy</p>
                </div>
                <div className="rounded-lg border border-border bg-card p-5">
                  <h3 className="text-sm font-medium mb-2">Redis</h3>
                  <p className="text-xs text-muted-foreground">Status: {(health.data.redis as Record<string, unknown>)?.status as string}</p>
                  <p className={`text-xs mt-1 ${(health.data.redis as Record<string, unknown>)?.healthy ? 'text-emerald-400' : 'text-red-400'}`}>
                    {(health.data.redis as Record<string, unknown>)?.healthy ? 'Healthy' : 'Unhealthy'}
                  </p>
                </div>
                <div className="rounded-lg border border-border bg-card p-5">
                  <h3 className="text-sm font-medium mb-2">Uptime</h3>
                  <p className="text-xs text-muted-foreground">{Math.round((health.data.uptime as number) / 3600)} hours</p>
                </div>
                <div className="rounded-lg border border-border bg-card p-5">
                  <h3 className="text-sm font-medium mb-2">Memory</h3>
                  <p className="text-xs text-muted-foreground">{health.data.memoryMB as number} MB heap used</p>
                </div>
              </div>
            )}
          </div>
        )}

        {activeTab === 'tickets' && (
          <div className="space-y-4">
            <h1 className="text-2xl font-bold">Support Tickets</h1>
            {(tickets?.data ?? []).length === 0 ? (
              <p className="text-sm text-muted-foreground py-8 text-center">No support tickets</p>
            ) : (
              <div className="space-y-2">
                {(tickets?.data ?? []).map((t) => (
                  <div key={t.id as string} className="rounded-lg border border-border bg-card p-4">
                    <div className="flex items-center justify-between">
                      <p className="text-sm font-medium">{t.subject as string}</p>
                      <span className={`rounded px-2 py-0.5 text-xs ${t.status === 'open' ? 'bg-red-900/30 text-red-400' : 'bg-emerald-900/30 text-emerald-400'}`}>{t.status as string}</span>
                    </div>
                    <p className="text-xs text-muted-foreground mt-1">{t.tenant_name as string} &middot; {t.user_email as string}</p>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {activeTab === 'announcements' && (
          <div className="space-y-4">
            <h1 className="text-2xl font-bold">Announcements</h1>
            {(announcements?.data ?? []).length === 0 ? (
              <p className="text-sm text-muted-foreground py-8 text-center">No announcements</p>
            ) : (
              <div className="space-y-2">
                {(announcements?.data ?? []).map((a) => (
                  <div key={a.id as string} className="rounded-lg border border-border bg-card p-4">
                    <p className="text-sm font-medium">{a.title as string}</p>
                    <p className="text-xs text-muted-foreground mt-1">{a.message as string}</p>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {activeTab === 'audit' && (
          <div className="space-y-4">
            <h1 className="text-2xl font-bold">Platform Audit Log</h1>
            {(auditLog?.data ?? []).length === 0 ? (
              <p className="text-sm text-muted-foreground py-8 text-center">No audit entries yet</p>
            ) : (
              <div className="space-y-2">
                {(auditLog?.data ?? []).map((e) => (
                  <div key={e.id as string} className="rounded-lg border border-border bg-card p-3 flex items-center justify-between">
                    <div>
                      <p className="text-xs font-medium">{e.action as string} on {e.entity_type as string}</p>
                      <p className="text-xs text-muted-foreground">{e.admin_name as string} ({e.admin_email as string})</p>
                    </div>
                    <span className="text-xs text-muted-foreground">{new Date(e.created_at as string).toLocaleString()}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {activeTab === 'analytics' && (
          <div className="space-y-4">
            <h1 className="text-2xl font-bold">Analytics</h1>
            <p className="text-sm text-muted-foreground">Detailed analytics charts coming soon. Use the Overview tab for key metrics.</p>
          </div>
        )}

        {activeTab === 'training' && (
          <div className="space-y-6">
            <h1 className="text-2xl font-bold">Training Analytics</h1>
            {!training ? (
              <p className="text-sm text-muted-foreground">Loading…</p>
            ) : (
              <>
                <div className="grid grid-cols-2 gap-4 md:grid-cols-5">
                  <StatCard label="Packages" value={training.data.totalPackages} icon={GraduationCap} />
                  <StatCard label="Assigned" value={training.data.totalAssignments} icon={FileCheck} />
                  <StatCard label="Completed" value={training.data.completedAssignments} icon={Activity} color="text-emerald-400" />
                  <StatCard label="Overdue" value={training.data.overdueAssignments} icon={Bell} color="text-red-400" />
                  <StatCard label="Completion Rate" value={`${training.data.completionRate}%`} icon={Shield} />
                </div>
                {training.data.averageCompletionTimeHours !== null && (
                  <p className="text-sm text-muted-foreground">
                    Average time to complete: {training.data.averageCompletionTimeHours} hours
                  </p>
                )}
                <div className="rounded-lg border border-border bg-card overflow-hidden">
                  <table className="w-full text-sm">
                    <thead className="bg-muted/30 text-xs uppercase tracking-wide">
                      <tr>
                        <th className="px-4 py-2 text-left">Tenant</th>
                        <th className="px-4 py-2 text-right">Packages</th>
                        <th className="px-4 py-2 text-right">Assigned</th>
                        <th className="px-4 py-2 text-right">Completed</th>
                        <th className="px-4 py-2 text-right">Overdue</th>
                        <th className="px-4 py-2 text-right">Completion %</th>
                      </tr>
                    </thead>
                    <tbody>
                      {training.data.byTenant.map((t) => (
                        <tr key={t.tenantId} className="border-t border-border">
                          <td className="px-4 py-2 font-medium">{t.tenantName}</td>
                          <td className="px-4 py-2 text-right">{t.packages}</td>
                          <td className="px-4 py-2 text-right">{t.assigned}</td>
                          <td className="px-4 py-2 text-right">{t.completed}</td>
                          <td className="px-4 py-2 text-right text-red-400">{t.overdue}</td>
                          <td className="px-4 py-2 text-right">{t.completionRate}%</td>
                        </tr>
                      ))}
                      {training.data.byTenant.length === 0 && (
                        <tr><td colSpan={6} className="px-4 py-8 text-center text-muted-foreground">No training activity across tenants yet.</td></tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </div>
        )}

        {activeTab === 'settings' && <PasswordChangeForm token={token} onLogout={handleLogout} />}
      </main>
    </div>
  );
}
